import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  Box,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  IconButton
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import SearchIcon from "@mui/icons-material/Search";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LinkIcon from "@mui/icons-material/Link";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useTranslation, TFunction } from "react-i18next";
import { CarSharingPaymentReservation, InvoiceServiceRequestFilter } from "../../types";
import { GridColDef, GridRenderCellParams, GridValueFormatterParams } from "@mui/x-data-grid";
import CarSharingDataGrid from "./CarSharingDataGrid";
import ViewListIcon from '@mui/icons-material/ViewList';
import { useTheme, Theme } from "@mui/material/styles";
import dayjs, { Dayjs } from "dayjs";
import { useQuery } from "@tanstack/react-query";
import { getPayments, getCarsharingClient } from "../../api/service/customerbaseApi";
import { CarsharingRedirectParams, redirectToCarsharing } from '../../../../util/carsharingService';
import { useCarSharingContext } from './CarSharingContext';

const textFallback = (value: string | null | undefined) => value ?? "-";

const decimalFormatter = ({ value }: GridValueFormatterParams<number | string>) => {
  if (value == null) return "-";
  const numericValue = Number(value);
  if (isNaN(numericValue)) return "-";
  return (numericValue / 100).toFixed(2);
};

const dateTimeValueFormatter = ({ value }: { value: string | null | undefined }) => {
  if (!value) return "-";
  return new Date(value).toLocaleString();
};

const dateValueFormatter = ({ value }: { value: string | null | undefined }) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString();
};

function isPaymentFailed(reservation: CarSharingPaymentReservation): boolean {
  return reservation.invoiceServiceDatas.some((serviceData) => {
    return (
      serviceData.serviceTypeID !== 48 &&
      serviceData.totalPrice !== 0 &&
      serviceData.transaction == null &&
      serviceData.canReiterate
    );
  });
}


const getReservationColumns = (t: TFunction, theme: Theme, onCorsaClick: (row: CarSharingPaymentReservation) => void): GridColDef<CarSharingPaymentReservation>[] => [
  {
    field: "reservationID",
    headerName: t("csPayments.headers.reservationID"),
    width: 150,
    renderCell: (params: GridRenderCellParams) => {
      const reservationID = Number(params.row.reservationID);
      const displayValue = reservationID === -1 ? t("csPayments.withoutRun") : reservationID;

      return (
        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
          <span>{displayValue}</span>
          <ViewListIcon
            sx={{ fontSize: 18, cursor: 'pointer', color: theme.palette.secondary.main }}
            onClick={() => onCorsaClick(params.row)}
          />
        </Box>
      );
    },
  },
  {
    field: "startROPName",
    headerName: t("csPayments.headers.startROPName"),
    flex: 1,
    minWidth: 150,
    valueGetter: (params) => textFallback(params.row.startROPName),
  },
  {
    field: "endROPName",
    headerName: t("csPayments.headers.endROPName"),
    flex: 1,
    minWidth: 150,
    valueGetter: (params) => textFallback(params.row.endROPName),
  },
  {
    field: "reservationDate",
    headerName: t("csPayments.headers.reservationDate"),
    width: 200,
    valueFormatter: dateValueFormatter,
    valueGetter: (params) => params.row.reservedFrom,
  },
  {
    field: "serviceDateFrom",
    headerName: t("csPayments.headers.serviceDateFrom"),
    width: 200,
    valueFormatter: dateTimeValueFormatter,
    valueGetter: (params) => params.row.invoiceServiceDatas[0]?.serviceDateFrom,
  },
  {
    field: "serviceDateTill",
    headerName: t("csPayments.headers.serviceDateTill"),
    width: 200,
    valueFormatter: dateTimeValueFormatter,
    valueGetter: (params) => params.row.invoiceServiceDatas[0]?.serviceDateTill,
  },
  {
    field: "resource",
    headerName: t("csPayments.headers.resource"),
    width: 150,
    valueGetter: (params) => textFallback(params.row.rotCaption),
  },
  {
    field: "features",
    headerName: t("csPayments.headers.features"),
    width: 150,
    valueGetter: (params) =>
      textFallback(params.row.features),
  },
  {
    field: "plate",
    headerName: t("csPayments.headers.plate"),
    width: 150,
    valueGetter: (params) =>
      textFallback(params.row.invoiceServiceDatas[0]?.carPlate),
  },
  {
    field: "totalPrice",
    headerName: t("csPayments.headers.totalPrice"),
    width: 150,
    valueFormatter: decimalFormatter,
  },
];

const getServiceColumns = (t: TFunction): GridColDef[] => [
  {
    field: "serviceID",
    headerName: t("csPayments.headers.serviceID"),
    width: 120,
    renderCell: (params) => {
      const service = params.row;
      const failed =
        service.serviceTypeID !== 48 &&
        service.totalPrice !== 0 &&
        service.transaction == null &&
        service.canReiterate

      return (
        <Box display="flex" alignItems="center">
          {failed && (
            <WarningAmberIcon fontSize="small" sx={{ color: "warning.main", mr: 0.5 }} />
          )}
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      );
    },
  },
  {
    field: "serviceTypeCaption",
    headerName: t("csPayments.headers.serviceTypeCaption"),
    flex: 1,
    minWidth: 150,
    valueGetter: (params) => params.row.invoiceItemDatas?.[0]?.text ?? "-",
  },
  {
    field: "quantity",
    headerName: t("csPayments.headers.quantity"),
    width: 100,
    valueGetter: (params) =>
      params.row?.invoiceItemDatas?.length
        ? params.row.invoiceItemDatas[0].quantity
        : "-",
    valueFormatter: decimalFormatter,
  },
  {
    field: "unit",
    headerName: t("csPayments.headers.unit"),
    width: 80,
    valueGetter: (params) => params.row?.unit ?? "-",
  },
  {
    field: "unitPrice",
    headerName: t("csPayments.headers.unitPrice"),
    width: 120,
    valueGetter: (params) =>
      params.row?.invoiceItemDatas?.length
        ? params.row.invoiceItemDatas[0].unitPrice
        : "-",
    valueFormatter: decimalFormatter,
  },
  {
    field: "totalPrice",
    headerName: t("csPayments.headers.totalPrice"),
    width: 120,
    valueGetter: (params) => params.row?.totalPrice ?? "-",
    valueFormatter: decimalFormatter,
  },
  {
    field: "serviceDateFrom",
    headerName: t("csPayments.headers.serviceDateFrom"),
    width: 180,
    valueGetter: (params) =>
      params.row?.serviceDateFrom ?? null,
    valueFormatter: dateTimeValueFormatter,
  },
  {
    field: "serviceDateTill",
    headerName: t("csPayments.headers.serviceDateTill"),
    width: 180,
    valueGetter: (params) =>
      params.row?.serviceDateTill ?? null,
    valueFormatter: dateTimeValueFormatter,
  },
  {
    field: "serviceValueFrom",
    headerName: t("csPayments.headers.serviceValueFrom"),
    width: 120,
    valueGetter: (params) => textFallback(params.row?.serviceValueFrom),
  },
  {
    field: "serviceValueTill",
    headerName: t("csPayments.headers.serviceValueTill"),
    width: 120,
    valueGetter: (params) => textFallback(params.row?.serviceValueTill),
  },
  {
    field: "serviceTypeID",
    headerName: t("csPayments.headers.serviceTypeID"),
    width: 150,
    valueGetter: (params) => params.row?.serviceTypeCaption ?? "-",
  },
  {
    field: "transaction",
    headerName: t("csPayments.headers.transaction"),
    width: 150,
    valueGetter: (params) => textFallback(params.row?.transaction),
  },
  {
    field: "note",
    headerName: t("csPayments.headers.note"),
    flex: 1,
    minWidth: 200,
    valueGetter: (params) => textFallback(params.row?.note),
  }

];

export default function CustomerBaseCarSharingPayments() {
  const { t } = useTranslation("customerbase");
  const { personId, contractId, setContractId, csCustomerId, language, contractData } = useCarSharingContext();
  const theme = useTheme();
  const [expanded, setExpanded] = React.useState(false);
  const [hasBeenOpened, setHasBeenOpened] = React.useState(false);

  React.useEffect(() => {
    if (expanded && !hasBeenOpened) {
      setHasBeenOpened(true);
    }
  }, [expanded, hasBeenOpened]);

  const handleRedirect = async () => {
    if (!csCustomerId) return;

    const params: CarsharingRedirectParams = {
      lang: language || 'it',
      type: 'CUSTOMER',
      section: 'PAYMENTS',
      id: String(csCustomerId)
    };

    try {
      await redirectToCarsharing(params);
    } catch (error) {
      console.error('Redirect failed:', error);
    }
  };

  const [selectedReservation, setSelectedReservation] = React.useState<CarSharingPaymentReservation | null>(null);

  const [filters, setFilters] = React.useState({
    period: "lastMonth",
    dateFrom: null as Dayjs | null,
    dateTo: null as Dayjs | null,
    reservationId: "",
    includeFailedPayments: false,
  });

  const [reservationIdError, setReservationIdError] = React.useState<string | null>(null);
  const [appliedFilters, setAppliedFilters] = React.useState<InvoiceServiceRequestFilter | null>(null);

  const { data: paymentsData, isError, error, isFetching } = useQuery(
    ['payments', appliedFilters, personId, contractId, language],
    () => getPayments(appliedFilters!, getCarsharingClient(), language || 'it'),
    { enabled: !!appliedFilters && !!personId && !!contractId, retry: 1 }
  );

  const handleSearch = () => {
    setReservationIdError(null);

    if (!personId) {
      return;
    }

    const reservationIdNumber = Number(filters.reservationId);

    if (filters.reservationId && (isNaN(reservationIdNumber) || reservationIdNumber < 1)) {
      setReservationIdError("Inserisci un numero valido maggiore o uguale a 1");
      return;
    }

    let finalDateFrom = filters.dateFrom;
    let finalDateTo = filters.dateTo;

    if (filters.period) {
      const now = dayjs();
      finalDateTo = now;

      switch (filters.period) {
        case "lastMonth":
          finalDateFrom = now.subtract(1, "month");
          break;
        case "last3Months":
          finalDateFrom = now.subtract(3, "month");
          break;
        case "last6Months":
          finalDateFrom = now.subtract(6, "month");
          break;
      }
    }

    const reservationIdFilter = reservationIdNumber >= 1 ? String(reservationIdNumber) : undefined;

    const filtersPayload: InvoiceServiceRequestFilter = {
      languageId: "10002",
      definedTime: filters.period || undefined,
      contractId: contractId ? [contractId.toString()] : undefined, personIds: personId ? [String(personId)] : undefined,
      dateInvoiceServiceFrom: finalDateFrom ? finalDateFrom.format("DD/MM/YYYY") : undefined,
      dateInvoiceServiceTill: finalDateTo ? finalDateTo.format("DD/MM/YYYY") : undefined,
      reservationid: reservationIdFilter,
    };

    setAppliedFilters(filtersPayload);
    setFilters(prev => ({ ...prev, includeFailedPayments: false }));
  };

  const displayedPayments = React.useMemo(() => {
    const payments = paymentsData?.invoiceReservationDatas ?? [];
    if (filters.includeFailedPayments) {
      return payments.filter(isPaymentFailed);
    }
    return payments;
  }, [paymentsData, filters.includeFailedPayments]);

  const handleCorsaClick = (row: CarSharingPaymentReservation) => {
    setSelectedReservation(row);
  };

  const handleBackClick = () => {
    setSelectedReservation(null);
  };

  const reservationColumns = getReservationColumns(t, theme, handleCorsaClick);
  const serviceColumns = getServiceColumns(t);

  const paymentsWithId = React.useMemo(() => {
    return displayedPayments.map((r, i) => ({
      ...r,
      id: `${r.reservationID}-${i}`,
      displayReservationID: r.reservationID === -1 ? t("csPayments.withoutRun") : r.reservationID,
    }));
  }, [displayedPayments, t]);

  const servicesWithId = React.useMemo(
    () =>
      (selectedReservation?.invoiceServiceDatas || []).map((s, i) => ({
        ...s,
        id: s.serviceID ?? i,
      })),
    [selectedReservation]
  );

  return (
    <Box sx={{ position: 'relative', zIndex: 1 }}>
      <Accordion disableGutters expanded={expanded} onChange={(_, isExpanded) => setExpanded(isExpanded)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Typography sx={{ fontWeight: 500 }}>{t("tabs.accordionsLabels.csPayments")}</Typography>
            {expanded && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  void handleRedirect();
                }}
                disabled={!csCustomerId}
                color="secondary"
              >
                <LinkIcon />
              </IconButton>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel id="contract-select-label">{t("csPayments.filters.number")}</InputLabel>
                <Select
                  labelId="contract-select-label"
                  value={contractId?.toString() ?? ''}
                  label={t("csPayments.filters.number")}
                  onChange={(e) => setContractId?.(Number(e.target.value))}
                >
                  {contractData?.map((contract) => {
                    const ownerType = contract.isOwner ? t("csPayments.owner") : t("csPayments.guest");
                    return (
                      <MenuItem key={contract.contractID} value={contract.contractID.toString()}>
                        {`${contract.contractNumber} - ${contract.contractTypeName} - ${ownerType}`}
                      </MenuItem>
                    );
                  })}

                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel>{t("csPayments.filters.period")}</InputLabel>
                <Select
                  value={filters.period}
                  label={t("csPayments.filters.period")}
                  onChange={(e) => {
                    const newPeriod = e.target.value;
                    setFilters(prev => ({ ...prev, period: newPeriod, dateFrom: null, dateTo: null }));
                  }}
                  MenuProps={{ disablePortal: true }}
                >
                  <MenuItem value="lastMonth">{t("csPayments.filters.periods.lastMonth")}</MenuItem>
                  <MenuItem value="last3Months">{t("csPayments.filters.periods.last3Months")}</MenuItem>
                  <MenuItem value="last6Months">{t("csPayments.filters.periods.last6Months")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <TextField
                fullWidth
                label={t("csPayments.filters.reservationId")}
                type="text"
                inputMode="numeric"
                value={filters.reservationId}
                onChange={(e) => setFilters(prev => ({ ...prev, reservationId: e.target.value.replace(/\D/g, "") }))}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  min: 1,
                }}
                error={!!reservationIdError}
                helperText={reservationIdError}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <LocalizationProvider dateAdapter={AdapterDayjs} dateFormats={{ normalDate: "DD/MM/YYYY" }}>
                <DatePicker
                  label={t("csPayments.filters.fromDate")}
                  value={filters.dateFrom}
                  minDate={dayjs('2019-01-01')}
                  maxDate={dayjs('2040-12-31')}
                  onChange={(newValue) => {
                    setFilters(prev => ({ ...prev, dateFrom: newValue, period: "" }));
                    if (newValue) {
                      setFilters(prev => ({ ...prev, dateTo: newValue }));
                    }
                  }}
                  inputFormat="DD/MM/YYYY"
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <LocalizationProvider dateAdapter={AdapterDayjs} dateFormats={{ normalDate: "DD/MM/YYYY" }}>
                <DatePicker
                  label={t("csPayments.filters.toDate")}
                  value={filters.dateTo}
                  onChange={(newValue) => {
                    setFilters(prev => ({ ...prev, dateTo: newValue, period: "" }));
                  }}
                  minDate={filters.dateFrom || undefined}
                  maxDate={filters.dateFrom ? filters.dateFrom.add(6, "month") : undefined}
                  inputFormat="DD/MM/YYYY"
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} container justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={!contractId || isFetching}
              >
                <SearchIcon />
              </Button>
            </Grid>
            <Grid item xs={12} container justifyContent="flex-end" sx={{ mx: 2, mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    sx={{ ml: 2 }}
                    checked={filters.includeFailedPayments}
                    onChange={(e) => setFilters(prev => ({ ...prev, includeFailedPayments: e.target.checked }))}
                  />}
                label={t("csPayments.filters.showOnlyFailed")}
                labelPlacement="start"
              />
            </Grid>          </Grid>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12}>
              {selectedReservation ? (
                <Box mb={2}>
                  <Button
                    onClick={handleBackClick}
                    variant="outlined"
                  >
                    <ArrowBackIcon />
                  </Button>
                </Box>
              ) : null}
              {!appliedFilters ? null : isFetching ? (
                <CircularProgress />
              ) : isError ? (
                <Alert severity="error">{(error as Error)?.message}</Alert>
              ) : (
                <CarSharingDataGrid
                  rows={selectedReservation ? servicesWithId : paymentsWithId}
                  columns={selectedReservation ? serviceColumns : reservationColumns}
                />
              )}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
